import { ImageScaleView, View, animate } from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import { BoosterId } from '../defs/booster';
import { getGameScene } from '../game/getGameScene';
import { Match2ButtonBadge, Match2ButtonBadgeState } from './Match2ButtonBadge';
import StateObserver from 'src/StateObserver';
import { GameEvent } from '../game/defs/event';
import { promptPurchaseBooster } from '../match2.sequence';
import sounds from 'src/lib/sounds';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { createWaiter } from 'src/lib/utils';
import uiConfig from 'src/lib/ui/config';
import bitmapFonts from 'src/lib/bitmapFonts';
import { formatDuration } from 'src/lib/formatDuration';
import { getMatch2TimedRewardRemainingTime } from 'src/replicant/match2/match2.getters';
import { duration } from 'src/replicant/utils/duration';
import { getUnlimitedBoosterUsedInSession } from '../match2.util';
import { matchTheme } from 'src/game/theme/match2/Match2Theme';

export class Match2BoosterButtonNew extends ButtonScaleView {
  private id!: BoosterId;
  private locked = false;
  private infinite = false;
  private selected = false;
  private boosterIcon: ImageScaleView;
  private badge: Match2ButtonBadge;
  private timerBanner: ImageScaleView;
  private timer: LangBitmapFontTextShadowView;
  private strikethrough: View;
  private waiter?: ReturnType<typeof createWaiter>;
  public onPurchaseComplete?: () => void;

  constructor() {
    const width = 114;
    const height = 114;

    super({
      width,
      height,
      image: matchTheme().getUIAsset('booster_btn.png'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.BOOSTER_SELECT,
      },
      onDown: () => {
        const anim = animate(this).clear();

        if (this.locked) {
          anim
            .now({ offsetX: 0 })
            .then({ offsetX: -3 }, 30)
            .then({ offsetX: 3 }, 50)
            .then({ offsetX: -3 }, 50)
            .then({ offsetX: 3 }, 50)
            .then({ offsetX: 0 }, 30);
        } else anim.then({ offsetY: 5 }, 50);
      },
      onUp: () => {
        if (this.locked) return;
        animate(this).clear().then({ offsetY: 0 }, 100);
      },
    });

    this.onClick = this.handleClick.bind(this);

    this.boosterIcon = new ImageScaleView({
      superview: this,
      width: 80,
      height: 80,
      centerOnOrigin: true,
      x: width * 0.5,
      y: height * 0.5,
    });

    this.badge = new Match2ButtonBadge({
      superview: this,
      scale: 1.2,
      x: width - 12,
      y: 15,
      noAnimation: true,
    });

    this.timerBanner = new ImageScaleView({
      superview: this,
      image: 'assets/ui/match2/frenzy/common/badge_frenzy_unlimited.png',
      width: 80,
      height: 37,
      centerOnOrigin: true,
      centerAnchor: true,
      x: width * 0.5,
      y: height - 2,
      scale: 1.3,
      visible: false,
    });

    this.timer = new LangBitmapFontTextShadowView({
      tag: 'powerupFrenzyTimer',
      superview: this,
      ...uiConfig.hud.energyText,
      size: 17.5,
      color: '#FFFFFF',
      width: 80,
      font: bitmapFonts(uiConfig.hud.energyText.fontName),
      align: 'center',
      centerAnchor: true,
      centerOnOrigin: true,
      x: width * 0.5 + 10,
      y: height - 9,
      visible: false,
      textShadowOffsetX: 0,
      textShadowOffsetY: 2,
      textShadowColor: '#26751E',
    });

    this.strikethrough = new View({
      superview: this,
      width: 105,
      height: 7,
      backgroundColor: 'rgba(255,0,0,0.75)',
      x: width * 0.5,
      y: height - 9,
      centerAnchor: true,
      centerOnOrigin: true,
      visible: false,
    });
  }

  public setBooster(id: BoosterId) {
    this.id = id;
    this.boosterIcon.setImage(matchTheme().getBoosterIcon(id));
    this.refresh();
  }

  public setLocked(locked: boolean) {
    this.locked = locked;
    this.refresh();
  }

  public setInteractive(v: boolean) {
    this.updateOpts({ canHandleEvents: v });
  }

  public refresh() {
    const unlimitedUsed = getUnlimitedBoosterUsedInSession(this.boosterId);
    const infiniteAvailable = this.infinite && !unlimitedUsed;
    let type: Match2ButtonBadgeState =
      infiniteAvailable || this.amount > 0 ? 'count' : 'add';
    if (this.locked) type = 'locked';
    else if (this.selected) type = 'active';
    this.badge.setState(type);
    this.setImage(matchTheme().getUIAsset('booster_btn.png'));
    this.strikethrough.style.visible = this.infinite && unlimitedUsed;
    this.badge.setCount(infiniteAvailable ? Infinity : this.amount);
  }

  private async handleClick() {
    if (this.locked) return;
    sounds.playSound(this.selected ? 'cancel' : 'ok');
    const unlimitedUsed = getUnlimitedBoosterUsedInSession(this.boosterId);
    const infiniteAvailable = this.infinite && !unlimitedUsed;
    let purchased = false;
    if (!infiniteAvailable && !this.amount) {
      purchased = await promptPurchaseBooster(this.id);

      this.onPurchaseComplete?.();
    }
    if (infiniteAvailable || this.amount) this.toggleBooster(false, purchased);
    this.refresh();
  }

  public toggleBooster(tutorialMode = false, purchased = false) {
    const game = getGameScene();
    if (!game) return;
    if (tutorialMode) {
      // Hide amount badge
      this.badge.updateOpts({ visible: false });
      // Make button non-interactable to prevent player from skipping the using of the booster
      this.updateOpts({ blockEvents: true });
    }
    this.selected = !this.selected;
    game.isBoosterTutorialActive = tutorialMode;
    if (this.selected) {
      game.events.subscribe(this.handleBoosterUsedBinded);
      game.playBooster(this.id);
      purchased && game.purchasedBooster(this.id);
    } else {
      game.events.unsubscribe(this.handleBoosterUsedBinded);
      game.cancelBooster();
    }
  }

  private handleBoosterUsedBinded = this.handleBoosterUsed.bind(this);
  private handleBoosterUsed(e?: GameEvent) {
    const game = getGameScene();
    if (!game || !e) return;
    if (e.id === 'booster' && !e.subtype) {
      // Resets for the tutorial mode, if previously active
      this.badge.updateOpts({ visible: true });
      this.updateOpts({ blockEvents: false });
      this.selected = false;
      this.refresh();
      game.events.unsubscribe(this.handleBoosterUsedBinded);
    }
  }

  public get isSelected() {
    return this.selected;
  }

  public get amount() {
    const { user } = StateObserver.getState();
    const { match2 } = user;
    return match2.boosters[this.id];
  }

  public get boosterId(): BoosterId {
    return this.id;
  }

  public autoUpdate() {
    const time = this.getRemainingTime();
    const infinite = time > 0;
    const changed = infinite !== this.infinite;
    this.infinite = infinite;
    this.timerBanner.style.visible = time > 0;
    this.timer.updateOpts({
      visible: time > 0,
      localeText: () => formatDuration(time),
    });
    if (changed) this.refresh();
    this.waiter = createWaiter();
    this.waiter.wait(this.autoUpdate.bind(this), duration({ seconds: 1 }));
  }

  public stopAutoUpdate() {
    this.waiter?.clear();
    this.waiter = undefined;
  }

  private getRemainingTime() {
    const user = StateObserver.getState().user;
    const now = StateObserver.now();
    return getMatch2TimedRewardRemainingTime(
      user,
      `boosters-${this.boosterId}`,
      now,
    );
  }
}
